import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';

interface EventPaginationProps {
  currentMonth: Date;
  availableMonths: Date[];
  totalItems: number;
  onMonthChange: (month: Date) => void;
}

const EventPagination: React.FC<EventPaginationProps> = ({
  currentMonth,
  availableMonths,
  totalItems,
  onMonthChange
}) => {
  // Helper function to check if two dates are in the same month
  const isSameMonth = (date1: Date, date2: Date): boolean => {
    return date1.getFullYear() === date2.getFullYear() && 
           date1.getMonth() === date2.getMonth();
  };

  // Get current month index in available months
  const currentMonthIndex = availableMonths.findIndex(month => isSameMonth(month, currentMonth));
  const isFirstMonth = currentMonthIndex === 0;
  const isLastMonth = currentMonthIndex === availableMonths.length - 1;

  // Format month name with proper capitalization
  const monthName = currentMonth.toLocaleString('en-US', { month: 'long' });
  const year = currentMonth.getFullYear();
  
  // Check if we need to show year (if there are months from different years)
  const hasMultipleYears = availableMonths.some(month => month.getFullYear() !== year);
  const displayText = hasMultipleYears ? `${monthName} ${year}` : monthName;

  // Navigate to previous month
  const handlePreviousMonth = () => {
    if (!isFirstMonth && currentMonthIndex > 0) {
      onMonthChange(availableMonths[currentMonthIndex - 1]);
    }
  };

  // Navigate to next month
  const handleNextMonth = () => {
    if (!isLastMonth && currentMonthIndex < availableMonths.length - 1) {
      onMonthChange(availableMonths[currentMonthIndex + 1]);
    }
  };

  // Don't show pagination if there's only one month or no items
  if (availableMonths.length <= 1 || totalItems === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-6">
        <button
          onClick={handlePreviousMonth}
          disabled={isFirstMonth}
          className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
          aria-label="Previous month"
        >
          <ChevronLeftIcon className="w-6 h-6" />
        </button>
        
        <div className="flex flex-col items-center gap-1 min-w-[200px]">
          <span className="text-white text-2xl font-serif font-medium tracking-wide">
            {displayText}
          </span>
          <span className="text-gray-400 text-sm">
            {totalItems} {totalItems === 1 ? 'date' : 'dates'}
          </span>
        </div>
        
        <button
          onClick={handleNextMonth}
          disabled={isLastMonth}
          className="p-2 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
          aria-label="Next month"
        >
          <ChevronRightIcon className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

export default EventPagination;
